import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, MenuItem, Paper, Stack, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { useUserAuth } from "../user_management/UserAuthContext";
import { shippingApi } from "./api";
import OrderDetailPanel from "./OrderDetailPanel";
import SourceHealthPanel from "./SourceHealthPanel";
import PackingStation from "./PackingStation";

export function moneyText(fact) {
  return fact?.value == null ? "Unknown" : `${fact.currency || "Currency unknown"} ${fact.value}`;
}

export function sourceTime(value) {
  // Preserve the source timestamp; don't silently interpret a timezone-less DB time as local time.
  return value || "Not recorded";
}

export function localStatusText(status) {
  return {
    NAMESPACE_NOT_CONFIGURED: "Local package records are not linked: SHIPPING_SOURCE_NAMESPACE is not configured.",
    SCHEMA_UNAVAILABLE: "Local package tables are unavailable. Imported orders remain readable.",
  }[status];
}

function packageTone(state) {
  if (["OUTGOING", "HANDED_OVER"].includes(state)) return "success";
  if (["PACKED", "SEALED", "CHECKED"].includes(state)) return "info";
  if (["HOLD", "VOID"].includes(state)) return "warning";
  return "default";
}

function operatorStatus(order) {
  if (!order.candidate_for_shipping) return { label: "Review", color: "warning" };
  const packages = order.packages || [];
  if (packages.some((row) => ["OUTGOING", "HANDED_OVER"].includes(row.physical_state))) return { label: "Outgoing", color: "success" };
  if (packages.some((row) => !["OUTGOING", "HANDED_OVER", "VOIDED"].includes(row.physical_state))) return { label: "In progress", color: "primary" };
  return { label: "Ready", color: "success" };
}

function friendlyService(value) {
  return {
    UNTRACKED_LETTER: 'Untracked · Letter',
    TRACKED_LETTER: 'Tracked · Letter',
    TRACKED_PARCEL: 'Tracked · Parcel',
  }[value] || null;
}

function queueShippingText(order) {
  const packageService = order.packages?.find((row) => row.effective_service)?.effective_service;
  return friendlyService(packageService) || 'Resolved at packing';
}

function ShippingLogin({ onSignedIn }) {
  const { login } = useUserAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError("");
    try { await login(email, password); setPassword(""); onSignedIn(); }
    catch (err) { setError(err.message || "Sign in failed."); }
    finally { setBusy(false); }
  }
  return <Paper variant="outlined" sx={{ p: 3, maxWidth: 440, borderRadius: 3 }}>
    <Typography variant="h6">Sign in to Shipping</Typography>
    <Typography variant="body2" color="text.secondary">Use your existing application account.</Typography>
    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    <Box component="form" onSubmit={submit}>
      <TextField fullWidth required type="email" label="Email" autoComplete="username" margin="normal"
        value={email} onChange={(e) => setEmail(e.target.value)} />
      <TextField fullWidth required type="password" label="Password" autoComplete="current-password" margin="normal"
        value={password} onChange={(e) => setPassword(e.target.value)} />
      <Button type="submit" variant="contained" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</Button>
    </Box>
  </Paper>;
}

function WorkspaceHeader({ initialView, capabilities, onOpenStation }) {
  const label = initialView === "review" ? "Review" : initialView === "packing" ? "Packing Station" : "Queue";
  return <Box sx={{ mb: 2 }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1.5}>
      <Box>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: "-0.025em" }}>Shipping</Typography>
          <Chip size="small" color="primary" variant="outlined" label={label} sx={{ fontWeight: 700 }} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
          {initialView === "review" ? "Orders that need attention before packing can continue." : initialView === "packing" ? "Continue your active package or start one from Queue." : "Find the next order and get it packed."}
        </Typography>
      </Box>
      {capabilities?.packing?.enabled && initialView !== "packing" && <Button variant="contained" size="large" onClick={onOpenStation} sx={{ minWidth: 170, borderRadius: 2 }}>
        Open Packing Station
      </Button>}
    </Stack>
  </Box>;
}

function VisibleQueueSummary({ orders }) {
  const summary = orders.reduce((acc, order) => {
    const status = operatorStatus(order).label;
    if (status === "Ready") acc.ready += 1;
    else if (status === "Review") acc.review += 1;
    else if (status === "In progress") acc.active += 1;
    else if (status === "Outgoing") acc.outgoing += 1;
    return acc;
  }, { ready: 0, review: 0, active: 0, outgoing: 0 });
  return <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Visible page</Typography>
    <Chip size="small" color="success" variant="outlined" label={`${summary.ready} ready`} />
    <Chip size="small" color="primary" variant="outlined" label={`${summary.active} in progress`} />
    <Chip size="small" color="warning" variant="outlined" label={`${summary.review} review`} />
    {summary.outgoing > 0 && <Chip size="small" color="success" label={`${summary.outgoing} outgoing`} />}
  </Stack>;
}

export default function ShippingDashboard({ initialView = "queue" }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(initialView === "review" ? "review" : "candidates");
  const [offsets, setOffsets] = useState([0]);
  const [revision, setRevision] = useState(0);
  const [visible, setVisible] = useState(!document.hidden);
  const [capabilities, setCapabilities] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);
  const [orderId, setOrderId] = useState(null);
  const [station, setStation] = useState(initialView === "packing" ? { orderId: null } : null);
  const [actionNotice, setActionNotice] = useState(null);
  const epoch = useRef(0);
  const openStation = useRef(null);
  openStation.current = { station, capabilities };
  const offset = offsets[offsets.length - 1];
  const failClosed = useCallback((err) => {
    epoch.current += 1;
    setCapabilities(null); setResult(null); setOrderId(null); setStation(null); setBusy(false); setError(err);
  }, []);

  useEffect(() => {
    const visibility = () => setVisible(!document.hidden);
    const storage = (event) => {
      if (event.key === "cmz_user_auth" || event.key === null) setRevision((value) => value + 1);
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("storage", storage);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("storage", storage);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const current = ++epoch.current;
    // A native phone camera can hide this tab. Keep the bound in-memory capture
    // mounted; foreground refresh and the station binding gate recheck authority.
    const preserveStation = Boolean(openStation.current.station && openStation.current.capabilities?.batches?.enabled);
    if (!visible && preserveStation) return () => controller.abort();
    if (!preserveStation) { setResult(null); setCapabilities(null); setOrderId(null); if (initialView !== "packing") setStation(null); }
    setError(null);
    if (!visible) { setBusy(false); return () => controller.abort(); }
    setBusy(true);
    (async () => {
      try {
        const caps = await shippingApi.capabilities(controller.signal);
        const data = await shippingApi.queue({ view: filter, q: query, offset, limit: 25 }, controller.signal);
        if (current !== epoch.current || controller.signal.aborted) return;
        if (preserveStation && caps.user.id !== openStation.current.capabilities.user.id) { failClosed({ status: 401, message: "Account changed. Reopen Shipping with the correct account." }); return; }
        setCapabilities(caps); setResult(data); setBusy(false);
      } catch (err) {
        if (err.name !== "AbortError" && current === epoch.current) failClosed(err);
      }
    })();
    return () => controller.abort();
  }, [filter, query, offset, revision, visible, failClosed, initialView]);

  useEffect(() => {
    if (!capabilities || !visible) return undefined;
    const controller = new AbortController();
    let live = true;
    let pending = false;
    const timer = setInterval(async () => {
      if (pending) return;
      pending = true;
      try {
        const caps = await shippingApi.capabilities(controller.signal);
        if (!live) return;
        if (caps.user.id !== capabilities.user.id) { setRevision((value) => value + 1); return; }
        // Quiet local polling: keep selection/detail open; never enqueue an eBay operation.
        if (!orderId) {
          const data = await shippingApi.queue({ view: filter, q: query, offset, limit: 25 }, controller.signal);
          if (live) setResult(data);
        }
      } catch (err) {
        if (live && err.name !== "AbortError") failClosed(err);
      } finally { pending = false; }
    }, Math.max(30, Math.min(300, capabilities.source_health?.poll_seconds || 60)) * 1000);
    return () => { live = false; clearInterval(timer); controller.abort(); };
  }, [capabilities, visible, failClosed, orderId, filter, query, offset]);

  function refresh() { setOffsets([0]); setRevision((value) => value + 1); }
  function applySearch(event) { event.preventDefault(); setQuery(search.trim()); refresh(); }
  function changeFilter(event) { setFilter(event.target.value); setOffsets([0]); }
  async function openPacking(targetOrderId = null) {
    setActionNotice(null);
    if (!targetOrderId) { setStation({ orderId: null }); setOrderId(null); return; }
    try {
      const active = await shippingApi.activeJob();
      if (active?.job && active.job.order_id !== targetOrderId) {
        setActionNotice({
          severity: 'info',
          message: `You already have Order ${active.job.order_id} · Package #${active.job.package_number} in progress. Resume or finish that package before starting ${targetOrderId}.`,
        });
        setStation({ orderId: null }); setOrderId(null); return;
      }
    } catch (_) {
      // PackingStation performs the authoritative server check; do not block on this explanatory preflight.
    }
    setStation({ orderId: targetOrderId }); setOrderId(null);
  }
  const notice = localStatusText(result?.local_records_status);
  const isReview = initialView === "review";

  return <Box sx={{ p: { xs: 1, md: 2 }, minWidth: 0, maxWidth: 1500, mx: "auto" }}>
    <WorkspaceHeader initialView={initialView} capabilities={capabilities} onOpenStation={() => openPacking()} />
    {!visible && !(station && capabilities?.batches?.enabled) ? <Typography>Shipping is hidden. Return to this tab to reload securely.</Typography> : <>
      {error && <Alert severity="error" sx={{ mb: 2 }} action={error.status !== 401 ? <Button color="inherit" onClick={refresh}>Retry</Button> : undefined}>
        {error.message || "Shipping could not be read. Retry when the connection is available."}
      </Alert>}
      {error?.status === 401 && <ShippingLogin onSignedIn={() => { if (initialView === "packing") setStation({ orderId: null }); refresh(); }} />}
      {busy && <Stack direction="row" alignItems="center" gap={1.5} role="status" sx={{ p: 2 }}><CircularProgress size={24} /><Typography>Reading Shipping…</Typography></Stack>}
      {capabilities && result && <>
        {notice && <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert>}
        {actionNotice && <Alert severity={actionNotice.severity} sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => openPacking()}>Resume active package</Button>}>{actionNotice.message}</Alert>}
        {isReview && <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          Review contains source holds and non-candidates such as fulfilled, unpaid or cancelled orders. Inspect the exact cause; this screen does not resolve source state.
        </Alert>}

        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", bgcolor: "background.paper" }}>
          <Box sx={{ px: { xs: 1.5, md: 2 }, pt: { xs: 1.5, md: 2 }, pb: 1.5 }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "flex-end" }} gap={1.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{isReview ? "Shipping Review" : "Shipping Queue"}</Typography>
                <Typography variant="body2" color="text.secondary">{isReview ? "Focus on the orders blocking normal packing." : "Find and start your next order."}</Typography>
              </Box>
              {!isReview && <VisibleQueueSummary orders={result.orders} />}
            </Stack>

            <Stack component="form" onSubmit={applySearch} direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} gap={1} sx={{ mt: 1.5 }}>
              <TextField size="small" placeholder="Search order, buyer, SKU or item…" aria-label="Search order, SKU or item" value={search} onChange={(e) => setSearch(e.target.value)}
                inputProps={{ maxLength: 100 }} sx={{ flex: 1, minWidth: 0 }} />
              {!isReview && <TextField select size="small" label="Show" value={filter} onChange={changeFilter} sx={{ minWidth: 180 }}>
                <MenuItem value="candidates">Ready / candidates</MenuItem><MenuItem value="all">All imported orders</MenuItem>
              </TextField>}
              <Button type="submit" variant="outlined">Search</Button>
              <Button variant="outlined" onClick={refresh}>Refresh</Button>
            </Stack>
          </Box>

          <Divider />
          {result.orders.length === 0 ? <Box sx={{ p: 2 }}><Alert severity="info">
            {result.next_offset != null ? "No matching orders in this search window. Continue to check older imported orders." : "No matching orders in the remaining imported data."}
          </Alert></Box> : <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label="Shipping orders" sx={{ minWidth: 960 }}>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: "grey.50", fontWeight: 750, color: "text.secondary", py: 1.35 } }}>
                  {['Order', 'Buyer', 'Items', 'Shipping', 'Status', 'Action'].map((label) => <TableCell key={label}>{label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>{result.orders.map((order) => {
                const status = operatorStatus(order);
                return <TableRow key={order.order_id} hover sx={{ "& td": { py: 1.35, verticalAlign: "middle" }, "&:hover": { bgcolor: "rgba(25,118,210,0.025)" } }}>
                  <TableCell sx={{ minWidth: 170 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{order.order_id}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div">{moneyText(order.money.goods_value)}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{order.buyer_username || 'Buyer not recorded'}</Typography>
                    {order.payment_status && order.payment_status !== 'PAID' && <Typography variant="caption" color="warning.main">{order.payment_status}</Typography>}
                  </TableCell>
                  <TableCell sx={{ minWidth: 300 }}>
                    {order.lines.slice(0, 2).map((line, index) => <Typography key={`${line.local_item_pk}-${index}`} variant="body2" sx={{ mb: 0.3, fontWeight: 500 }}>
                      {line.ordered_sale_unit_quantity} × {line.sku || "Missing SKU"} — {line.title || "Untitled item"}
                    </Typography>)}
                    {order.lines.length > 2 && <Typography variant="caption" color="text.secondary">+{order.lines.length - 2} more sale lines</Typography>}
                  </TableCell>
                  <TableCell sx={{ minWidth: 165 }}>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>{queueShippingText(order)}</Typography>
                    <Typography variant="caption" color="text.secondary">Buyer postage {moneyText(order.money.shipping_paid)}</Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Chip size="small" color={status.color} variant={status.label === "Ready" ? "filled" : "outlined"} label={status.label} sx={{ fontWeight: 700 }} />
                    {!order.candidate_for_shipping && order.issues.slice(0, 1).map((issue, i) => <Typography variant="caption" color="warning.main" component="div" sx={{ mt: 0.45 }} key={`${issue.code}-${i}`}>
                      {issue.message || issue.code}
                    </Typography>)}
                    {order.packages?.length > 0 && <Stack direction="row" flexWrap="wrap" gap={0.4} sx={{ mt: 0.5 }}>{order.packages.slice(0, 2).map((p) => <Chip size="small" key={p.id} color={packageTone(p.physical_state)} variant="outlined" label={`#${p.package_number} · ${p.physical_state}`} />)}</Stack>}
                  </TableCell>
                  <TableCell sx={{ minWidth: 190 }}>
                    <Stack direction="row" gap={0.75} justifyContent="flex-end">
                      {!isReview && capabilities.packing?.enabled && order.candidate_for_shipping && status.label === 'Ready' && <Button size="small" variant="contained" onClick={() => openPacking(order.order_id)} aria-label={`Open packing for order ${order.order_id}`}>Pack</Button>}
                      {!isReview && capabilities.packing?.enabled && order.candidate_for_shipping && status.label === 'In progress' && <Button size="small" variant="contained" onClick={() => openPacking(order.order_id)} aria-label={`Resume packing order ${order.order_id}`}>Resume</Button>}
                      {!isReview && capabilities.packing?.enabled && order.candidate_for_shipping && status.label === 'Outgoing' && <Button size="small" variant="outlined" onClick={() => openPacking(order.order_id)} aria-label={`Open package for order ${order.order_id}`}>Open package</Button>}
                      <Button size="small" variant="text" onClick={() => setOrderId(order.order_id)} aria-label={`View order ${order.order_id}`}>Details</Button>
                    </Stack>
                  </TableCell>
                </TableRow>;
              })}</TableBody>
            </Table>
          </TableContainer>}

          <Divider />
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1} sx={{ px: { xs: 1.5, md: 2 }, py: 1.35 }}>
            <Typography variant="body2" color="text.secondary">Page {offsets.length} · {result.orders.length} shown</Typography>
            <Stack direction="row" gap={1}>
              <Button size="small" variant="outlined" disabled={offsets.length < 2} onClick={() => setOffsets((values) => values.slice(0, -1))}>Previous</Button>
              <Button size="small" variant="outlined" disabled={result.next_offset == null} onClick={() => setOffsets((values) => [...values, result.next_offset])}>
                {result.scan_limit_reached ? "Continue search" : "Next"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ mt: 1.5, borderRadius: 3, overflow: "hidden" }}>
          <Box component="details">
            <Box component="summary" sx={{ cursor: "pointer", px: 2, py: 1.25, fontWeight: 700, color: "text.secondary", userSelect: "none" }}>System status & source safety</Box>
            <Divider />
            <Stack spacing={1.25} sx={{ p: 2 }}>
              <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                <Chip size="small" variant="outlined" label={`Signed in · ${capabilities.user.email}`} />
                <Chip size="small" variant="outlined" label={capabilities?.packing?.enabled ? `Packing · ${capabilities.packing.mode}` : "Packing disabled"} />
              </Stack>
              <Typography variant="body2" color="text.secondary">Queue status comes from imported order snapshots. Packing performs its own source and ownership checks.</Typography>
              {capabilities.source_health && <SourceHealthPanel compact capabilities={capabilities} onAccessLost={failClosed} />}
              <Typography variant="caption" color="text.secondary">DB timestamps do not prove a recent eBay check. Pages can change after an import; Refresh restarts from the newest order.</Typography>
            </Stack>
          </Box>
        </Paper>

        {orderId && <OrderDetailPanel key={orderId} orderId={orderId} capabilities={capabilities} onClose={() => setOrderId(null)} onAccessLost={failClosed}
          onPack={() => openPacking(orderId)} />}
        {station && capabilities.packing?.enabled && <PackingStation key={station.orderId || "active"} capabilities={capabilities} orderId={station.orderId}
          onClose={() => { setStation(null); refresh(); }} onAccessLost={failClosed} />}
      </>}
    </>}
  </Box>;
}
